import { useEffect, useRef, useState } from 'react';
import { BookOpen, ShoppingCart, ExternalLink } from 'lucide-react';
import { siteData } from '../data/mock';
import { FreePreviewSection } from './FreePreviewSection';

export const BookSection = () => {
  const [visible, setVisible] = useState(false);
  const [buyVisible, setBuyVisible] = useState(false);
  const [blurbVisible, setBlurbVisible] = useState(false);
  const sectionRef = useRef(null);
  const buyRef = useRef(null);
  const blurbRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setVisible(true);
      },
      { threshold: 0.1 }
    );
    const buyObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setBuyVisible(true);
      },
      { threshold: 0.15 }
    );
    const blurbObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setBlurbVisible(true);
      },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    if (buyRef.current) buyObserver.observe(buyRef.current);
    if (blurbRef.current) blurbObserver.observe(blurbRef.current);
    return () => {
      observer.disconnect();
      buyObserver.disconnect();
      blurbObserver.disconnect();
    };
  }, []);

  return (
    <>
      {/* THE BOOK Section */}
      <section id="book" ref={sectionRef} className="relative py-20 md:py-40" style={{ background: '#0d0f12' }}>
        {/* Top separator */}
        <div className="absolute top-0 left-5 right-5 md:left-10 md:right-10 h-px bg-gradient-to-r from-transparent via-[rgba(0,212,255,0.15)] to-transparent" />

        <div className="max-w-[87.5rem] mx-auto px-5 md:px-10">
          {/* Section Header - Full Width */}
          <div
            className={`mb-12 md:mb-20 transition-all duration-1000 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <p className="font-montserrat text-xs tracking-[0.3em] uppercase text-[#00d4ff] mb-4 md:mb-6">
              The Book
            </p>
            <h2 className="font-montserrat font-900 text-3xl md:text-7xl lg:text-[6.5rem] uppercase text-[#e8e6e1] leading-[0.82] tracking-tight">
              The Silent Cipher
            </h2>
            <p className="font-montserrat font-600 text-base md:text-2xl lg:text-3xl uppercase text-[#00d4ff] mt-3 md:mt-4 tracking-wide">
              Decoding a Killer Written in Scripture and Silicon
            </p>
          </div>

          {/* Book Content - Split Layout */}
          <div className="flex flex-col lg:flex-row gap-10 lg:gap-24 items-start">
            {/* Left: Cover */}
            <div
              className={`lg:w-2/5 flex flex-col items-center lg:items-start transition-all duration-1000 delay-300 ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
            >
              <div className="relative">
                {/* Glow */}
                <div
                  className="absolute -inset-6 opacity-15 blur-2xl"
                  style={{ background: 'radial-gradient(ellipse, #00d4ff, transparent 70%)' }}
                />
                <img
                  src={siteData.images.bookCoverAlt}
                  alt="The Silent Cipher Cover"
                  className="relative z-10 w-[250px] md:w-[360px] shadow-2xl mx-auto"
                  style={{
                    boxShadow: '0 20px 50px rgba(0,0,0,0.5), 0 0 60px rgba(0,212,255,0.06)',
                  }}
                />
              </div>
            </div>

            {/* Right: Synopsis */}
            <div className="lg:w-3/5">
              <div className="space-y-6">
                {siteData.book.description.map((para, i) => {
                  const isHighlight = i === 3;
                  return (
                    <p
                      key={i}
                      className={`transition-all duration-1000 ${
                        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
                      } ${
                        isHighlight
                          ? 'font-montserrat font-700 text-xl md:text-2xl text-[#00d4ff] pl-6 border-l-2 border-[#00d4ff]/30 italic'
                          : 'font-cardo text-lg text-[#b0ada6] leading-relaxed'
                      }`}
                      style={{ transitionDelay: `${400 + i * 150}ms` }}
                    >
                      {para}
                    </p>
                  );
                })}
              </div>

              {/* Triptych Image */}
              <div
                className={`mt-10 md:mt-16 transition-all duration-1000 delay-700 ${
                  visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
              >
                <img
                  src={siteData.images.triptych}
                  alt="Ancient Verses, Modern Surveillance, Deadly Codes"
                  className="w-full max-w-xl rounded-sm opacity-90"
                  style={{
                    boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
                  }}
                />
              </div>
            </div>
          </div>

          {/* Full Blurb Section */}
          <div
            ref={blurbRef}
            className={`mt-16 md:mt-32 pt-10 md:pt-16 border-t border-[rgba(255,255,255,0.05)] transition-all duration-1000 ${
              blurbVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <div className="flex flex-col md:flex-row gap-8 md:gap-12 items-start">
              <div className="md:w-1/3">
                <img
                  src={siteData.images.sanskritCipher}
                  alt="Dharma Artha Kama Moksha - An Ancient Cyclical Cipher"
                  className="w-full max-w-[200px] md:max-w-xs rounded-sm opacity-80"
                />
              </div>
              <div className="md:w-2/3">
                <p className="font-montserrat text-xs tracking-[0.3em] uppercase text-[#c4a962] mb-6">
                  From the back cover
                </p>
                <p className="font-cardo text-base md:text-lg text-[#999] leading-[1.8] italic">
                  {siteData.book.blurb}
                </p>
                <p className="font-cardo text-sm text-[#555] mt-8">
                  A sleek, atmospheric fusion of code, karma, conspiracy, and the dark mathematics of fate.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FREE PREVIEW Section */}
      <FreePreviewSection />

      {/* BUY Section */}
      <section
        id="buy"
        ref={buyRef}
        className="relative py-20 md:py-32"
        style={{ background: '#0a0c0f' }}
      >
        {/* Top separator */}
        <div className="absolute top-0 left-5 right-5 md:left-10 md:right-10 h-px bg-gradient-to-r from-transparent via-[rgba(0,212,255,0.15)] to-transparent" />

        <div className="max-w-[87.5rem] mx-auto px-5 md:px-10">
          <div className="flex flex-col lg:flex-row gap-12 lg:gap-20 items-center">
            {/* Left: Heading */}
            <div
              className={`lg:w-2/5 transition-all duration-1000 ${
                buyVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
            >
              <p className="font-montserrat text-xs tracking-[0.3em] uppercase text-[#00d4ff] mb-4">
                Get Your Copy
              </p>
              <h2 className="font-montserrat font-800 text-3xl md:text-5xl uppercase text-[#e8e6e1] leading-[0.85] mb-6">
                Buy The<br />
                <span className="text-[#00d4ff]">Silent Cipher</span>
              </h2>
              <p className="font-cardo text-base md:text-lg text-[#888680] leading-relaxed">
                Available now across major platforms. Choose your preferred retailer below.
              </p>
            </div>

            {/* Right: Purchase Buttons */}
            <div
              className={`lg:w-3/5 w-full max-w-lg transition-all duration-1000 delay-200 ${
                buyVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
            >
              <div className="flex flex-col gap-5">
                {/* Button 1: Amazon — Primary */}
                <a
                  href="https://www.amazon.in/dp/B0GN9TKHM9"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group relative block w-full no-underline"
                >
                  <div className="absolute -inset-1 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-xl" style={{ background: 'radial-gradient(ellipse, rgba(0,212,255,0.15), transparent 70%)' }} />
                  <div className="relative flex items-center gap-4 w-full py-5 md:py-6 px-6 md:px-8 rounded-2xl bg-[#00d4ff] text-[#0d0f12] transition-all duration-300 group-hover:scale-[1.02] group-hover:shadow-[0_0_50px_rgba(0,212,255,0.25)] active:scale-[0.98]">
                    <ShoppingCart size={22} className="flex-shrink-0" />
                    <div className="flex-1">
                      <span className="font-montserrat font-bold text-base md:text-lg tracking-wide uppercase block">
                        Buy on Amazon
                      </span>
                      <span className="font-montserrat text-xs text-[#0d0f12]/60 tracking-wide">
                        Available Worldwide
                      </span>
                    </div>
                    <ExternalLink size={16} className="flex-shrink-0 opacity-50 group-hover:opacity-100 transition-opacity duration-300" />
                  </div>
                </a>

                {/* Button 2: Flipkart */}
                <a
                  href="https://www.flipkart.com/the-silent-cipher/p/itm0d2d49fadff8f"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group relative block w-full no-underline"
                >
                  <div className="relative flex items-center gap-4 w-full py-4 md:py-5 px-6 md:px-8 rounded-xl border border-[rgba(0,212,255,0.2)] bg-[rgba(0,212,255,0.04)] text-[#e8e6e1] transition-all duration-300 group-hover:bg-[rgba(0,212,255,0.08)] group-hover:border-[rgba(0,212,255,0.4)] group-hover:scale-[1.01] active:scale-[0.98]">
                    <ShoppingCart size={20} className="flex-shrink-0 text-[#00d4ff]" />
                    <div className="flex-1">
                      <span className="font-montserrat font-semibold text-sm md:text-base tracking-wide uppercase block">
                        Buy on Flipkart
                      </span>
                      <span className="font-montserrat text-[11px] text-[#888680] tracking-wide">
                        Available in India
                      </span>
                    </div>
                    <ExternalLink size={14} className="flex-shrink-0 text-[#555] group-hover:text-[#00d4ff] transition-colors duration-300" />
                  </div>
                </a>

                {/* Button 3: Notion Press */}
                <a
                  href="https://notionpress.com/in/read/the-silent-cipher/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group relative block w-full no-underline"
                >
                  <div className="relative flex items-center gap-4 w-full py-4 md:py-5 px-6 md:px-8 rounded-xl border border-[rgba(196,169,98,0.2)] bg-[rgba(196,169,98,0.03)] text-[#e8e6e1] transition-all duration-300 group-hover:bg-[rgba(196,169,98,0.07)] group-hover:border-[rgba(196,169,98,0.4)] group-hover:scale-[1.01] active:scale-[0.98]">
                    <BookOpen size={20} className="flex-shrink-0 text-[#c4a962]" />
                    <div className="flex-1">
                      <span className="font-montserrat font-semibold text-sm md:text-base tracking-wide uppercase block">
                        Buy Limited Print Edition
                      </span>
                      <span className="font-montserrat text-[11px] text-[#888680] tracking-wide">
                        Available on Notion Press
                      </span>
                    </div>
                    <ExternalLink size={14} className="flex-shrink-0 text-[#555] group-hover:text-[#c4a962] transition-colors duration-300" />
                  </div>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};
